import { cn } from "@repo/design-system/utils";
import { List, Table2 } from "lucide-react";
import { memo } from "react";

export type CartView = "cart" | "compare";

export interface CartViewToggleProps {
  /** Which view is active. */
  view: CartView;
  /** Switch views. CartPage maps this onto the `?tab` param, so the toggle is
   * deep-linkable and back-button friendly (KAN-72). */
  onViewChange: (view: CartView) => void;
  className?: string;
}

const OPTION_BASE =
  "inline-flex items-center gap-1.5 rounded-md p-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500";
const OPTION_ON = "bg-white text-purple-700 shadow-sm";
const OPTION_OFF = "text-gray-500 hover:text-gray-700";

/**
 * The List <-> Table segmented toggle (KAN-72). It replaces the old cart tab
 * switch: "List view" shows the seller cards, "Table view" shows the full-width
 * Items x Suppliers matrix. It drives the `?tab` param, so the two views are
 * deep-linkable and back-button friendly.
 */
export const CartViewToggle = memo(function CartViewToggle({
  view,
  onViewChange,
  className
}: CartViewToggleProps) {
  return (
    <div
      role="group"
      aria-label="Cart view"
      className={cn(
        "inline-flex min-w-[210px] justify-between rounded-lg bg-gray-100 p-1",
        className
      )}
    >
      <button
        type="button"
        onClick={() => onViewChange("cart")}
        aria-pressed={view === "cart"}
        className={cn(OPTION_BASE, view === "cart" ? OPTION_ON : OPTION_OFF)}
      >
        <List className="h-4 w-4" aria-hidden="true" />
        List view
      </button>
      <button
        type="button"
        onClick={() => onViewChange("compare")}
        aria-pressed={view === "compare"}
        className={cn(OPTION_BASE, view === "compare" ? OPTION_ON : OPTION_OFF)}
      >
        <Table2 className="h-4 w-4" aria-hidden="true" />
        Table view
      </button>
    </div>
  );
});
